import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronDown, ChevronLeft, ChevronRight, ChevronsUpDown, ChevronUp,
  Fingerprint, Loader2, RefreshCw, Search, Users,
} from "lucide-react";
import { useHrGetStaffQuery } from "../api/hrApi";
import { fmtTime, staffName } from "../utils/hr";

const PER_PAGE = 15;

type SortKey = "name" | "staff_no" | "department" | "biometric" | "status";
type SortDir = "asc" | "desc";

function SortHeader({
  label, col, sort, onSort,
}: { label: string; col: SortKey; sort: { key: SortKey; dir: SortDir }; onSort: (k: SortKey) => void }) {
  const active = sort.key === col;
  const Icon = !active ? ChevronsUpDown : sort.dir === "asc" ? ChevronUp : ChevronDown;
  return (
    <th className="px-4 py-2 font-medium text-left">
      <button
        onClick={() => onSort(col)}
        className={`inline-flex items-center gap-1 hover:text-gray-900 transition-colors ${active ? "text-gray-900" : ""}`}
      >
        {label}
        <Icon className={`w-3.5 h-3.5 ${active ? "text-indigo-600" : "text-gray-300"}`} />
      </button>
    </th>
  );
}

/**
 * Secretariat staff. The people themselves are managed under Admins — this is
 * HR's view of them, and the place to set the biometric id and work window that
 * attendance is matched and measured against.
 */
export default function HrStaffPage() {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({ key: "name", dir: "asc" });

  const { data, isLoading, isFetching, isError, refetch } = useHrGetStaffQuery({});
  const rows = useMemo(() => data?.rows ?? [], [data]);

  const filtered = useMemo(() => {
    const nq = q.trim().toLowerCase();
    return rows.filter((s: any) => {
      if (status && String(s.status ?? "").toLowerCase() !== status) return false;
      if (!nq) return true;
      const blob = [staffName(s), s.staff_no, s.biometric_id, s.job_title, s.user?.email, s.department?.name]
        .filter(Boolean).join(" ").toLowerCase();
      return blob.includes(nq);
    });
  }, [rows, q, status]);

  const sorted = useMemo(() => {
    const dir = sort.dir === "asc" ? 1 : -1;
    const str = (v: any) => String(v ?? "").toLowerCase();
    return [...filtered].sort((a: any, b: any) => {
      switch (sort.key) {
        case "staff_no":   return str(a.staff_no).localeCompare(str(b.staff_no), undefined, { numeric: true }) * dir;
        case "department": return str(a.department?.name).localeCompare(str(b.department?.name)) * dir;
        // Unenrolled staff sort last on the way up — they're the ones to chase.
        case "biometric":  return (str(a.biometric_id) || "zzz").localeCompare(str(b.biometric_id) || "zzz") * dir;
        case "status":     return str(a.status).localeCompare(str(b.status)) * dir;
        default:           return staffName(a).localeCompare(staffName(b), undefined, { sensitivity: "base" }) * dir;
      }
    });
  }, [filtered, sort]);

  const pageCount = Math.max(1, Math.ceil(sorted.length / PER_PAGE));
  const safePage = Math.min(page, pageCount);
  const paged = useMemo(
    () => sorted.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE),
    [sorted, safePage]
  );

  useEffect(() => { setPage(1); }, [q, status, sort]);

  const onSort = (key: SortKey) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));

  const unenrolled = rows.filter((s: any) => !s.biometric_id).length;

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6">
      <div className="bg-white border rounded-xl p-6">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-indigo-50 border border-indigo-100">
              <Users className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800">Staff</h1>
              <p className="text-sm text-gray-500 mt-0.5">
                {rows.length} secretariat staff · people are created and edited under{" "}
                <Link to="/admins" className="text-indigo-600 hover:underline">Admins</Link>
              </p>
            </div>
          </div>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border hover:bg-gray-50 text-sm"
          >
            <RefreshCw className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

        {/* Biometric enrolment nudge — attendance imports only match enrolled staff. */}
        {unenrolled > 0 && (
          <div className="mt-4 flex items-center gap-2 px-4 py-2.5 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
            <Fingerprint className="w-4 h-4 shrink-0" />
            {unenrolled} staff member{unenrolled !== 1 ? "s have" : " has"} no biometric id — their device rows
            can't be matched on import.
          </div>
        )}

        {/* Filters */}
        <div className="mt-5 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, staff no, biometric id, job title…"
              className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-gray-100"
            />
          </div>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="sm:w-48 px-3 py-2 border rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-gray-100"
          >
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="mt-8 py-10 flex justify-center text-gray-300">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : isError ? (
          <div className="mt-6 text-sm text-red-600">Failed to load staff.</div>
        ) : sorted.length === 0 ? (
          <div className="mt-6 p-4 rounded-md border bg-gray-50 text-sm text-gray-700">
            {rows.length === 0 ? "No staff records yet." : "No staff match your filters."}
          </div>
        ) : (
          <>
            <div className="mt-5 border rounded-xl overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b bg-gray-50/60">
                    <SortHeader label="Staff" col="name" sort={sort} onSort={onSort} />
                    <SortHeader label="Staff No" col="staff_no" sort={sort} onSort={onSort} />
                    <SortHeader label="Department" col="department" sort={sort} onSort={onSort} />
                    <SortHeader label="Biometric ID" col="biometric" sort={sort} onSort={onSort} />
                    <th className="px-4 py-2 font-medium">Work Window</th>
                    <SortHeader label="Status" col="status" sort={sort} onSort={onSort} />
                    <th className="px-4 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paged.map((s: any) => (
                    <tr key={s.id} className="hover:bg-gray-50/60">
                      <td className="px-4 py-3">
                        <Link to={`/hr/staff/${s.id}`} className="font-medium text-gray-900 hover:text-indigo-600">
                          {staffName(s)}
                        </Link>
                        <div className="text-xs text-gray-400">{s.job_title || s.user?.email || ""}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{s.staff_no || <span className="text-gray-300">—</span>}</td>
                      <td className="px-4 py-3 text-gray-700">
                        {s.department?.name || <span className="text-gray-300">—</span>}
                        {s.designation?.name && <div className="text-xs text-gray-400">{s.designation.name}</div>}
                      </td>
                      <td className="px-4 py-3">
                        {s.biometric_id ? (
                          <span className="font-mono text-xs text-gray-800">{s.biometric_id}</span>
                        ) : (
                          <span className="text-xs text-amber-600 font-medium">Not enrolled</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        {s.work_start || s.work_end
                          ? `${fmtTime(s.work_start)} – ${fmtTime(s.work_end)}`
                          : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center px-2 py-1 text-xs border rounded-full ${
                            String(s.status).toLowerCase() === "active"
                              ? "bg-green-100 text-green-800 border-green-200"
                              : "bg-gray-100 text-gray-600 border-gray-200"
                          }`}
                        >
                          {s.status ?? "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end">
                          <Link
                            to={`/hr/staff/${s.id}`}
                            className="px-3 py-1.5 rounded-lg border text-xs hover:bg-gray-50 whitespace-nowrap"
                          >
                            HR details
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4">
              <p className="text-xs text-gray-500">
                Showing <span className="font-medium text-gray-700">{(safePage - 1) * PER_PAGE + 1}</span>–
                <span className="font-medium text-gray-700">{Math.min(safePage * PER_PAGE, sorted.length)}</span>{" "}
                of <span className="font-medium text-gray-700">{sorted.length}</span>
              </p>
              {pageCount > 1 && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={safePage === 1}
                    className="p-1.5 rounded-lg border bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    aria-label="Previous page"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  {Array.from({ length: pageCount }, (_, i) => i + 1)
                    .filter((n) => n === 1 || n === pageCount || Math.abs(n - safePage) <= 1)
                    .map((n, i, arr) => (
                      <React.Fragment key={n}>
                        {i > 0 && n - arr[i - 1] > 1 && <span className="px-1 text-xs text-gray-300">…</span>}
                        <button
                          onClick={() => setPage(n)}
                          className={`min-w-[2rem] px-2 py-1.5 rounded-lg border text-xs font-medium ${
                            n === safePage ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-600 hover:bg-gray-50"
                          }`}
                        >
                          {n}
                        </button>
                      </React.Fragment>
                    ))}
                  <button
                    onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                    disabled={safePage === pageCount}
                    className="p-1.5 rounded-lg border bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    aria-label="Next page"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
